/**
 * Query Thread Component - CR-1403, CR-2220
 *
 * Chat-style display of query messages between customer and supplier:
 * - Message history with sender info
 * - Reply form with text input
 * - File attachment support
 * - Status indicator (open/responded/closed)
 * - CR-2220: Delete and strikethrough functionality
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Loader2,
  Send,
  MessageSquare,
  Paperclip,
  Clock,
  CheckCircle,
  XCircle,
  User,
  MoreVertical,
  Trash2,
  Strikethrough,
  Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import type { RFQQuery, RFQQueryMessage } from '../../lib/api';

interface QueryThreadProps {
  query: RFQQuery;
  currentUserId: number;
  onRefresh?: () => void;
}

export default function QueryThread({ query, currentUserId, onRefresh }: QueryThreadProps) {
  const messages = query.messages || [];
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getStatusBadge = () => {
    switch (query.status) {
      case 'open':
        return (
          <Badge variant="outline" className="border-blue-500 text-blue-600">
            <Clock className="h-3 w-3 mr-1" />
            Open
          </Badge>
        );
      case 'responded':
        return (
          <Badge variant="outline" className="border-green-500 text-green-600">
            <CheckCircle className="h-3 w-3 mr-1" />
            Responded
          </Badge>
        );
      case 'closed':
        return (
          <Badge variant="secondary">
            <XCircle className="h-3 w-3 mr-1" />
            Closed
          </Badge>
        );
      default:
        return null;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };


  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      await rfqAPI.respondToQuery(query.id, newMessage.trim());
      setNewMessage('');
      toast.success('Message sent');

      // Refresh to get updated messages
      onRefresh?.();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCloseQuery = async () => {
    try {
      setClosing(true);
      await rfqAPI.closeQuery(query.id);
      toast.success('Query closed');
      onRefresh?.();
    } catch (error) {
      console.error('Error closing query:', error);
      toast.error('Failed to close query');
    } finally {
      setClosing(false);
    }
  };

  // CR-2220: Delete message handler
  const handleDeleteMessage = async (messageId: number) => {
    try {
      setActionLoading(messageId);
      await rfqAPI.deleteQueryMessage(messageId);
      toast.success('Message deleted');
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    } finally {
      setActionLoading(null);
    }
  };

  // CR-2220: Strikethrough message handler
  const handleStrikethroughMessage = async (messageId: number) => {
    try {
      setActionLoading(messageId);
      await rfqAPI.strikethroughQueryMessage(messageId);
      toast.success('Message marked as struck through');
      onRefresh?.();
    } catch (error) {
      console.error('Error striking through message:', error);
      toast.error('Failed to strikethrough message');
    } finally {
      setActionLoading(null);
    }
  };

  // CR-2220: Remove strikethrough handler
  const handleRemoveStrikethrough = async (messageId: number) => {
    try {
      setActionLoading(messageId);
      await rfqAPI.removeStrikethroughQueryMessage(messageId);
      toast.success('Strikethrough removed');
      onRefresh?.();
    } catch (error) {
      console.error('Error removing strikethrough:', error);
      toast.error('Failed to remove strikethrough');
    } finally {
      setActionLoading(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // CR-2220: Render message actions dropdown for current user's messages
  const renderMessageActions = (message: RFQQueryMessage, isCurrentUser: boolean) => {
    if (!isCurrentUser || query.status === 'closed') return null;

    const isLoading = actionLoading === message.id;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <MoreVertical className="h-3 w-3" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {message.is_strikethrough ? (
            <DropdownMenuItem onClick={() => handleRemoveStrikethrough(message.id)}>
              <Undo2 className="h-4 w-4 mr-2" />
              Remove Strikethrough
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => handleStrikethroughMessage(message.id)}>
              <Strikethrough className="h-4 w-4 mr-2" />
              Strikethrough
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => handleDeleteMessage(message.id)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">{query.subject}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge()}
            {query.status !== 'closed' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCloseQuery}
                disabled={closing}
              >
                {closing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  'Close Query'
                )}
              </Button>
            )}
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          Started on {new Date(query.created_at).toLocaleDateString()}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Messages */}
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No messages yet
            </p>
          ) : (
            messages.map((message) => {
              const senderId = typeof message.sender === 'number' ? message.sender : message.sender;
              const isCurrentUser = senderId === currentUserId;
              const senderName = message.sender_name || 'User';

              return (
                <div
                  key={message.id}
                  className={`flex gap-3 group ${isCurrentUser ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${isCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    <User className="h-4 w-4" />
                  </div>
                  <div
                    className={`flex-1 max-w-[80%] ${
                      isCurrentUser ? 'text-right' : ''
                    }`}
                  >
                    <div className={`flex items-start gap-1 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                      <div
                        className={`rounded-lg p-3 ${
                          isCurrentUser
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        } ${message.is_strikethrough ? 'opacity-60' : ''}`}
                      >
                        <p className={`text-sm whitespace-pre-wrap ${message.is_strikethrough ? 'line-through' : ''}`}>
                          {message.message}
                        </p>
                        {/* CR-2220: Show strikethrough indicator */}
                        {message.is_strikethrough && (
                          <p className="text-xs mt-1 italic opacity-70">
                            Struck through by {message.strikethrough_by_name}
                          </p>
                        )}
                      </div>
                      {/* CR-2220: Message actions dropdown */}
                      {renderMessageActions(message, isCurrentUser)}
                    </div>
                    <div className={`flex items-center gap-2 mt-1 text-xs text-muted-foreground ${isCurrentUser ? 'justify-end' : ''}`}>
                      {!isCurrentUser && <span>{senderName}</span>}
                      <span>{formatTimestamp(message.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Form */}
        {query.status !== 'closed' && (
          <div className="border-t pt-4">
            <div className="flex gap-2">
              <Textarea
                placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                className="min-h-[80px] resize-none"
                disabled={sending}
              />
            </div>
            <div className="flex justify-between items-center mt-2">
              <Button variant="outline" size="sm" disabled>
                <Paperclip className="h-4 w-4 mr-2" />
                Attach File
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={sending || !newMessage.trim()}
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {query.status === 'closed' && (
          <div className="border-t pt-4 text-center text-sm text-muted-foreground">
            This query has been closed. No further messages can be added.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
